// Client -> Edge Function telemetry.
// - Web vitals are only sent after statistics consent.
// - Runtime errors are also gated by consent (safe default; documented in Datenschutz).
// - Never sends stacks, form input, filenames, emails, tokens.
import { hasStatisticsConsent } from "./consent";

const ENDPOINT = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/telemetry`;

const MAX_MSG = 500;
const MAX_PATH = 200;

let errorBudget = 5; // per-session cap
const throttleWindow = new Map<string, number>();

function sanitize(str: string | undefined | null, max: number): string | undefined {
  if (!str) return undefined;
  // strip common PII patterns
  const cleaned = str
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]")
    .replace(/\b(?:\d[ -]*?){13,19}\b/g, "[card]")
    .replace(/(sb-[a-z0-9-]+-auth-token|Bearer\s+[A-Za-z0-9._-]+)/gi, "[token]");
  return cleaned.slice(0, max);
}

function sanitizePath(path: string | undefined): string | undefined {
  if (!path) return undefined;
  const noQuery = path.split("?")[0].split("#")[0];
  return noQuery.slice(0, MAX_PATH);
}

async function post(body: Record<string, unknown>): Promise<void> {
  try {
    await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // swallow — telemetry must never break the app
  }
}

export interface WebVitalPayload {
  name: "CLS" | "FCP" | "LCP" | "TTFB" | "INP";
  value: number;
  rating: string;
}

export function reportWebVital(v: WebVitalPayload): void {
  if (!hasStatisticsConsent()) return;
  post({
    type: "web_vital",
    name: v.name,
    value: Number(v.value.toFixed(2)),
    rating: v.rating,
    path: sanitizePath(window.location.pathname),
  });
}

export interface RuntimeErrorPayload {
  name: "runtime_error" | "react_error_boundary";
  message: string;
  error_code?: string;
  path?: string;
}

export function reportRuntimeError(e: RuntimeErrorPayload): void {
  if (!hasStatisticsConsent()) return;
  if (errorBudget <= 0) return;

  const key = `${e.name}|${(e.message || "").slice(0, 80)}`;
  const now = Date.now();
  const last = throttleWindow.get(key) ?? 0;
  if (now - last < 60_000) return;
  throttleWindow.set(key, now);
  errorBudget -= 1;

  post({
    type: "runtime_error",
    name: e.name,
    message: sanitize(e.message, MAX_MSG),
    error_code: sanitize(e.error_code, 100),
    path: sanitizePath(e.path ?? window.location.pathname),
  });
}
